import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Zap, TrendingUp, Clock, Copy } from "lucide-react";
import { SamayPravahGraph } from "../../contexts/ChatContext";

interface Props {
  graph: SamayPravahGraph;
  guidance?: string;
  timestamp?: Date;
  onCopy?: () => void;
}

const MOVEMENT_COLORS: Record<string, string> = {
  inward: "#7BA7FF",
  outward: "#FFB84D",
  steady: "#C9B7FF",
};

const PHASE_COLORS: Record<string, string> = {
  light: "#F4C97A",
  medium: "#FFCFA1",
  heavy: "#D8B4D2",
};

const FLOW_COLORS: Record<string, string> = {
  rising: "#A8A4D8",
  settling: "#A8E6A3",
  scattered: "#FFD27F",
};

const BARS = [
  { 
    label: "Energy Movement", 
    key: "movement" as const, 
    colors: MOVEMENT_COLORS, 
    defaultColor: "#FFB84D",
    icon: Zap,
    duration: 0.95
  },
  { 
    label: "Phase Weight", 
    key: "phase_weight" as const, 
    colors: PHASE_COLORS, 
    defaultColor: "#F4C97A",
    icon: TrendingUp,
    duration: 0.85
  },
  { 
    label: "Flow Direction", 
    key: "flow_direction" as const, 
    colors: FLOW_COLORS, 
    defaultColor: "#A8A4D8",
    icon: Clock,
    duration: 1.05
  },
];

const SamayPravahEnergyGraph: React.FC<Props> = ({ graph, guidance, timestamp, onCopy }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [animatedValues, setAnimatedValues] = useState<Record<string, number>>({});
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Animation timing constants
  const ANIMATION = {
    entry: {
      fadeIn: 180,
      headerSlide: 140,
      graphFade: 220,
      barStagger: 120,
    },
    barFill: {
      movement: 0.95,
      phase_weight: 0.85,
      flow_direction: 1.05,
    },
    microPulse: {
      duration: 1.8,
      opacity: 0.08,
    },
  };

  // Get intensity values with fallbacks
  const getIntensity = useCallback((key: string) => {
    const data = graph[key as keyof SamayPravahGraph];
    if (!data || typeof data !== 'object') return 0;
    return Math.min(100, Math.max(0, (data as any).intensity || 0));
  }, [graph]);

  // Get type for color selection
  const getType = useCallback((key: string) => {
    const data = graph[key as keyof SamayPravahGraph];
    if (!data || typeof data !== 'object') return undefined;
    return (data as any).type;
  }, [graph]);

  // Trigger entry animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Animate bar values sequentially with stagger
  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    const startAnimation = async () => {
      for (let i = 0; i < BARS.length; i++) {
        const bar = BARS[i];
        const target = getIntensity(bar.key);
        const duration = ANIMATION.barFill[bar.key as keyof typeof ANIMATION.barFill] || 0.9;
        
        // Stagger delay before each bar
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, ANIMATION.entry.barStagger));
        }

        // Animate this bar
        await animateBar(bar.key, target, duration);
      }
      setHasAnimated(true);
    };

    startAnimation();
  }, [isVisible, getIntensity, hasAnimated]);

  // Animate a single bar from 0 to target value
  const animateBar = (key: string, target: number, duration: number): Promise<void> => {
    return new Promise((resolve) => {
      const startTime = performance.now();
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        
        // Ease-in-out curve
        const easeInOut = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        const currentValue = target * easeInOut;
        
        setAnimatedValues(prev => ({
          ...prev,
          [key]: currentValue,
        }));

        // Also update the DOM element directly for smoother visual
        const barIndex = BARS.findIndex(b => b.key === key);
        const ref = barRefs.current[barIndex];
        if (ref) {
          ref.style.width = `${currentValue}%`;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setAnimatedValues(prev => ({
            ...prev,
            [key]: target,
          }));
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  };

  // Scroll handler for header shrink effect
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const scrollPosition = window.scrollY;
        setScrollY(Math.min(Math.max(scrollPosition - rect.top, 0), 4));
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Format guidance text
  const formatGuidance = (text: string) => {
    if (!text) return null;
    return text.split("\n").filter(p => p.trim().length > 0);
  };

  // Get bar color
  const getBarColor = (bar: typeof BARS[0]) => {
    const type = getType(bar.key);
    return type && bar.colors[type] ? bar.colors[type] : bar.defaultColor;
  };

  // Calculate header shrink
  const headerShrink = Math.min(scrollY / 4, 1);
  const headerOpacity = Math.min(scrollY / 2, 0.18);

  // Check if we have valid data
  const hasValidData = graph && typeof graph === 'object' && Object.keys(graph).length > 0;
  if (!hasValidData) return null;

  // Format timestamp
  const formatTimestamp = (date?: Date) => {
    if (!date) return null;
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <motion.div
      ref={containerRef}
      className="relative w-full my-6 rounded-2xl overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: ANIMATION.entry.fadeIn / 1000, ease: "easeOut" }}
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(0,0,0,0.09)",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Scroll shadow overlay */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          opacity: headerOpacity,
          boxShadow: "inset 0 4px 20px rgba(109, 76, 146, 0.1)",
        }}
      />

      {/* Header - Click to toggle */}
      <motion.div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        initial={{ y: 8, opacity: 0 }}
        animate={{ 
          y: isVisible ? -headerShrink * 4 : 8, 
          opacity: isVisible ? 1 : 0 
        }}
        transition={{ duration: ANIMATION.entry.headerSlide / 1000, ease: "easeOut" }}
        style={{
          borderBottom: isExpanded ? "1px solid rgba(0,0,0,0.07)" : "none",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(109, 76, 146, 0.12)" }}>
            <span className="text-[#6D4C92] text-sm">🪷</span>
          </div>
          <div>
            <h3 className="font-semibold text-[#1A1A2E] text-sm">Samay Pravah</h3>
            <p className="text-xs text-[#999]">Energy Flow Analysis</p>
          </div>
        </div>
        <button
          className="p-1 rounded-full hover:bg-black/5 transition-colors"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#666]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#666]" />
          )}
        </button>
      </motion.div>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0">
              {/* Graph section */}
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: isVisible ? 1 : 0 }}
                transition={{ 
                  duration: ANIMATION.entry.graphFade / 1000, 
                  delay: 0.1 
                }}
              >
                {/* Subtitle */}
                <div style={{ marginBottom: "12px" }}>
                  <span style={{ fontSize: "12px", color: "#999", fontStyle: "italic" }}>
                    Aaj ka flow kaisa chal raha hai...
                  </span>
                </div>

                {/* Dasha / Sub-Dasha — only shown when the backend could
                    compute a real chart from the user's birth data */}
                {(graph.dasha || graph.sub_dasha) && (
                  <div
                    className="flex items-center gap-4 flex-wrap"
                    style={{
                      marginBottom: "4px",
                      padding: "8px 12px",
                      borderRadius: "10px",
                      background: "rgba(109, 76, 146, 0.06)",
                    }}
                  >
                    {graph.dasha && (
                      <span style={{ fontSize: "12.5px", color: "#4A3B5C" }}>
                        <strong style={{ color: "#6D4C92" }}>Dasha:</strong> {graph.dasha}
                      </span>
                    )}
                    {graph.sub_dasha && (
                      <span style={{ fontSize: "12.5px", color: "#4A3B5C" }}>
                        <strong style={{ color: "#6D4C92" }}>Sub-Dasha:</strong> {graph.sub_dasha}
                      </span>
                    )}
                  </div>
                )}

                {/* Bars */}
                {BARS.map((bar, index) => {
                  const intensity = getIntensity(bar.key);
                  const animatedValue = animatedValues[bar.key] || 0;
                  const color = getBarColor(bar);
                  const isActive = isHovered === bar.key;
                  const Icon = bar.icon;

                  return (
                    <motion.div
                      key={bar.key}
                      className="relative"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: index * 0.12,
                        duration: 0.3,
                        ease: "easeOut",
                      }}
                      onMouseEnter={() => setIsHovered(bar.key)}
                      onMouseLeave={() => setIsHovered(null)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Label with icon */}
                        <div className="flex items-center gap-2" style={{ width: "130px", flexShrink: 0 }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: color }} />
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {bar.label}
                          </span>
                        </div>

                        {/* Track */}
                        <div
                          style={{
                            flex: 1,
                            height: "13px",
                            borderRadius: "7px",
                            background: "rgba(0,0,0,0.07)",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          {/* Bar fill */}
                          <div
                            ref={(el) => { barRefs.current[index] = el; }}
                            style={{
                              height: "100%",
                              width: `${animatedValue}%`,
                              borderRadius: "7px",
                              background: color,
                              position: "relative",
                              transition: "none", // We control animation via JS
                            }}
                          >
                            {/* Micro-pulse glow */}
                            <motion.div
                              className="absolute inset-0 rounded-full pointer-events-none"
                              style={{
                                backgroundColor: color,
                                opacity: ANIMATION.microPulse.opacity,
                              }}
                              animate={{
                                scale: [1, 1.02, 1],
                                opacity: [ANIMATION.microPulse.opacity, 0.04, ANIMATION.microPulse.opacity],
                              }}
                              transition={{
                                duration: ANIMATION.microPulse.duration,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                            />
                          </div>

                          {/* Ripple effect on tap - shows when hovered after click */}
                          {isActive && (
                            <motion.div
                              className="absolute inset-0 rounded-full pointer-events-none"
                              style={{
                                backgroundColor: color,
                                opacity: 0.12,
                              }}
                              initial={{ scale: 0.8, opacity: 0.12 }}
                              animate={{ scale: 1.2, opacity: 0 }}
                              transition={{ duration: 0.16 }}
                            />
                          )}
                        </div>

                        {/* Percent */}
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            fontWeight: 600,
                            width: "36px",
                            textAlign: "right",
                            flexShrink: 0,
                          }}
                        >
                          {Math.round(animatedValue)}%
                        </span>
                      </div>
                    </motion.div>
                  );
                })}

                {/* Guidance text with fade-in */}
                {guidance && guidance.trim() && (
                  <motion.div
                    className="mt-4 pt-3 border-t border-black/5"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: 0.3 }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: "rgba(109, 76, 146, 0.1)" }}
                      >
                        <span className="text-[#6D4C92] text-xs">🪷</span>
                      </div>
                      <div className="space-y-1">
                        {formatGuidance(guidance)?.map((paragraph, idx) => (
                          <motion.p
                            key={idx}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              delay: 0.3 + idx * 0.08,
                              duration: 0.2,
                            }}
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              color: "#555",
                              fontStyle: "italic",
                              lineHeight: "1.65",
                            }}
                          >
                            {paragraph}
                          </motion.p>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer: Timestamp and Copy button */}
      <div
        className="flex items-center justify-between px-4 py-2 border-t border-gray-100"
      >
        <span className="text-xs text-gray-500">
          {formatTimestamp(timestamp)}
        </span>
        {onCopy && (
          <button
            onClick={onCopy}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Copy"
          >
            <Copy className="w-3.5 h-3.5 text-[#6D4C92]" />
          </button>
        )}
      </div>

      {/* Focus glow styles */}
      <style>{`
        .samay-pravah-focus-glow {
          transition: box-shadow 0.18s ease-out;
        }
        .samay-pravah-focus-glow:focus-within {
          box-shadow: 0 0 0 2px rgba(109, 76, 146, 0.15);
        }
        
        @keyframes breathe-pulse {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.04; }
        }
      `}</style>
    </motion.div>
  );
};

export default SamayPravahEnergyGraph;